import { observer } from 'mobx-react';
import React, { useCallback, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import categoriesStore from '../../store/categoriesStore';
import { SCREENS } from 'src/constants/screens';
import integrationStore from 'src/mobx/integrationStore';
import { localeStore } from 'src/mobx/localesStore';
import { modalStore } from 'src/mobx/modalStore';
import { history } from 'src/services/history';
import { TCategory } from 'src/types/categories';
import { TBooleanInNumber } from 'src/types/main';
import { confirmDelete } from 'src/utils/modalConfirmations';
import Switcher from 'src/view/components/Controls/Switcher/Switcer';
import DeleteRestoreBlock from 'src/view/components/DeleteRestoreBlock/DeleteRestoreBlock';
import IntegrationManager from 'src/view/components/IntegrationManager/IntegrationManager';
import Text, { TEXT } from 'src/view/components/Text/Text';
import './style.scss';

enum NUMBER_BLOCK {
  ITEMS = 'items',
  MENU_GROUPS = 'menu_groups',
}

interface IProps {
  category?: TCategory;
}

const CategoriesListRow: React.FC<IProps> = observer((props: IProps): JSX.Element => {
  const switcherFrozen = useMemo(() => {
    return Boolean(props.category?.is_delete);
  }, [props.category]);

  const editCategory = () => {
    if (!props.category) return;
    history.push(SCREENS.CATEGORIES_EDIT(props.category.id));
  };

  const deleteCategory = () => {
    confirmDelete({
      message: localeStore.t('confirm_deleting_category'),
      onConfirm: () => props.category && categoriesStore.deleteCategory(props.category.id),
      redConfirmBtn: true,
    });
  };

  const restoreCategory = () => {
    if (!props.category || categoriesStore.isLoading) return;
    categoriesStore.restoreCategory(props.category.id);
  };

  const changeStatus = useCallback(
    (status: TBooleanInNumber) => {
      if (!props.category || switcherFrozen) return;
      categoriesStore.changeStatus(props.category.id, status);
    },
    [props.category, switcherFrozen]
  );

  const openCategoryItemsModal = useCallback(() => {
    if (!props.category?.items) return;

    modalStore.openCategoryItemsModal({
      category: { ...props.category, items: props.category.items },
      items: props.category.items,
    });
  }, [props.category]);

  const openCategoryMenuGroupsModal = useCallback(() => {
    if (!props.category?.global_categories) return;
    modalStore.openCategoryMenuGroupsModal({
      menuGroups: props.category.global_categories,
    });
  }, [props.category]);

  const renderClickableNumberBlock = useCallback(
    (type: NUMBER_BLOCK): JSX.Element => {
      if (!props.category) {
        return <Skeleton width={54} height={30} style={{ borderRadius: 10 }} />;
      }

      const blockNumber: number | undefined = type === NUMBER_BLOCK.ITEMS ? props.category.items?.length : props.category.global_categories?.length;

      if (blockNumber) {
        return (
          <div
            className="categories-list-row__num-label clickable"
            onClick={() => (type === NUMBER_BLOCK.ITEMS ? openCategoryItemsModal() : openCategoryMenuGroupsModal())}
          >
            <p className="family-medium text-size-12 color-dark letter-spacing-12">{blockNumber}</p>
          </div>
        );
      }

      return (
        <div className="categories-list-row__num-label">
          <Text type={TEXT.LIST_ROW} text={blockNumber ?? 0} />
        </div>
      );
    },
    [props.category, openCategoryItemsModal, openCategoryMenuGroupsModal]
  );

  const renderAvailabilitySwitcher = useCallback((): JSX.Element | null => {
    if (!props.category) return <Skeleton width={50} height={30} style={{ borderRadius: 30 }} />;

    if (props.category.is_delete) return null;

    return <Switcher disabled={switcherFrozen || categoriesStore.isLoading} status={props.category.status} onChange={changeStatus} />;
  }, [props.category, switcherFrozen, categoriesStore.isLoading, changeStatus]);

  const {
    categoryAvailability: availabilityRestrictionAccess,
    categoryDelete: deleteRestrictionAccess,
    categoryRestore: restoreRestrictionAccess,
  } = integrationStore.posRestrictions || {};

  return (
    <div className="categories-list-row d-flex full-width">
      <div
        onClick={() => (props.category?.is_delete ? undefined : editCategory())}
        className={`d-flex flex-row align-items-center ${props.category?.is_delete ? 'w-70' : 'cursor-pointer w-25'}`}
      >
        {props.category ? <Text type={TEXT.LIST_ROW} text={props.category.name} className="pr-2" /> : <Skeleton width={150} height={19.2} />}
      </div>
      {!props.category?.is_delete ? (
        <>
          <div className="d-flex flex-row align-items-center w-20">{renderClickableNumberBlock(NUMBER_BLOCK.ITEMS)}</div>
          <div className="d-flex flex-row align-items-center w-20">{renderClickableNumberBlock(NUMBER_BLOCK.MENU_GROUPS)}</div>
          <div className="d-flex flex-row align-items-center w-15">
            {props.category ? <p className="family-medium text-size-16 color-dark">{props.category.sort}</p> : <Skeleton width={40} height={19.2} />}
          </div>
        </>
      ) : null}
      <div className={`d-flex flex-row align-items-center justify-content-${props.category?.is_delete ? 'end w-30' : 'between w-20'}`}>
        <IntegrationManager setting={availabilityRestrictionAccess} withTooltip>
          {renderAvailabilitySwitcher()}
        </IntegrationManager>
        <IntegrationManager
          setting={props.category?.is_delete ? restoreRestrictionAccess : deleteRestrictionAccess}
          tooltipOptions={{ minWidth: 355 }}
          withTooltip
        >
          <DeleteRestoreBlock
            skeleton={!props.category}
            disabled={categoriesStore.isLoading}
            isDelete={props.category?.is_delete}
            onRestore={restoreCategory}
            onDelete={deleteCategory}
          />
        </IntegrationManager>
      </div>
    </div>
  );
});

export default CategoriesListRow;
